import type { RecordContactTarget } from "./RecordCommunications";
import { useWidgetDismiss } from "../lib/use-widget-dismiss";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { apiFetchWithTimeout } from "../lib/api-client";
import { disconnectedPhoneData, type PhoneData } from "./communications-model";
import { PhoneTextInbox } from "./PhoneTextInbox";
import "./communications.css";

type TextQueue = {
  companyPhone: string | null;
  hasMore?: boolean;
  messaging?: PhoneData["messaging"];
  items: Array<{ blocked?: boolean | number; id: string; customerName: string | null; phone: string; unread: number; status?: string; lastDirection?: string | null; summary: string | null }>;
};
export function TextsWorkspace({ timezone, canReply, canAttachPhotos = false, initialThreadId, initialRecipient, withJob = false, onClose }: { timezone: string; canReply: boolean; canAttachPhotos?: boolean; initialThreadId?: string | null; initialRecipient?: RecordContactTarget | null; withJob?: boolean; onClose?: () => void }) {
  const panel = useRef<HTMLElement>(null);
  useWidgetDismiss(panel, true, withJob ? '[data-workspace-widget-trigger="texts"], .job-detail-dialog' : '[data-workspace-widget-trigger="texts"]', () => onClose?.());
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panel.current?.focus();
    return () => { if (document.activeElement === document.body) previous?.focus(); };
  }, []);
  const [data, setData] = useState<PhoneData>(disconnectedPhoneData);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    let timer: number | undefined;
    const refresh = async () => {
      try {
        const response = await apiFetchWithTimeout("/api/v1/workspace/communications/live-sms?page=0&view=needs_response", { signal: controller.signal });
        if (!response.ok) throw new Error("Texts could not be loaded. Please try again.");
        const payload = await response.json() as { data: TextQueue };
        if (controller.signal.aborted) return;
        setData({ companyPhone: payload.data.companyPhone, connected: Boolean(payload.data.companyPhone), calls: [], hasMore: payload.data.hasMore, messaging: payload.data.messaging, threads: payload.data.items.map(item => ({ ...item, messages: item.summary ? [{ id: `${item.id}:latest`, body: item.summary, direction: item.lastDirection === "outbound" ? "outbound" : "inbound", createdAt: "" }] : [] })) });
        setError("");
        // Poll only after a successful response. Retrying a failing endpoint
        // every ten seconds hides the problem and can keep the panel unusable.
        timer = window.setTimeout(() => void refresh(), 10000);
      } catch (cause) {
        if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : "Texts could not be loaded.");
      } finally { if (!controller.signal.aborted) setLoading(false); }
    };
    void refresh();
    return () => { controller.abort(); if (timer) window.clearTimeout(timer); };
  }, [revision]);
  const markRead = useCallback((id: string) => {
    setData(current => ({ ...current, threads: current.threads.map(thread => thread.id === id ? { ...thread, unread: 0 } : thread) }));
    window.dispatchEvent(new Event("workview:texts-changed"));
  }, []);
  return createPortal(<section ref={panel} tabIndex={-1} className="comms-texts-popover" data-job-companion={withJob ? "true" : undefined} role="dialog" aria-modal="false" aria-label="Texts" onKeyDown={event => { if (event.key === "Escape" && !event.defaultPrevented) { event.preventDefault(); event.stopPropagation(); closeRef.current?.(); } }}>
    <header className="comms-texts-page-header"><div><h1>Texts</h1></div>{onClose && <button type="button" className="comms-icon-button" onClick={onClose} aria-label="Close texts"><X size={20} /></button>}</header>
    {loading && !initialRecipient && !initialThreadId && <p role="status" className="comms-muted">Loading texts…</p>}
    {error && <p role="alert" className="comms-review-error">{error} <button type="button" className="comms-link" onClick={() => setRevision(value => value + 1)}>Try again</button></p>}
    {(!loading || initialRecipient || initialThreadId) && <PhoneTextInbox data={data} timezone={timezone} canReply={canReply} canAttachPhotos={canAttachPhotos} initialThreadId={initialThreadId} initialRecipient={initialRecipient} query={query} setQuery={setQuery} onRead={markRead} />}
  </section>, document.body);
}
